import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import smileRingtoneUri from '../../assets/audio/smile-ringtone.mp3?url';
import { gracefullyPlayAudio } from '../../audio';
import { NOTIFICATION_VOLUME } from '../../config';
import { logger } from '../../datadog/logger';
import { useGetTelehealthSessionInfoCallback } from '../../hooks';
import { twilioSyncClientAtom } from '../../session';
import { debounce } from '../../utils';
import { roomTokenAtom } from '../../video-sdk';
import { DebugMutex } from '../../video-sdk/core/mutex';
import { zoomTokenAtom } from '../../video-sdk/zoom';
import { hideSystemNotificationsPermissionUserGesturePromptToast } from '../notifications';
import {
  VisitorState,
  apiTokenAtom,
  sessionAppointmentShortIdAtom,
  sessionConversationSidAtom,
  visitorAccessDocSidAtom,
  visitorStateAtom,
} from '../state';
import { admissionTrackingHistory } from './admission/analytics/AdmissionTrackingHistory';
import { useShowAdmissionSystemNotificationCallback } from './admission/notifications/useShowAdmissionSystemNotificationCallback';
import { admissionAttemptsAtom } from './admission/state';
import { useVisitorConnectRoomCallback } from './admission/useVisitorConnectRoomCallback';

const LOAD_TIME_MS = 1000;
const DEBOUNCE_TIME_MS = 1000;

enum VisitorAccessDocEventType {
  GRANTED = 'access-granted',
  DENIED = 'access-denied',
}

type VisitorAccessDocData = {
  eventType: VisitorAccessDocEventType;
  token: string;
  /**
   * A token that can be used to communicate with the Telehealth API as a client
   * user
   */
  apiToken?: string;
  conversationSid?: string;
  zoomJwt: string;

  /**
   * A unique ID generated on the provider side to represent the admission
   * transaction end-to-end. Used in user tracking.
   */
  admissionTransactionId?: string;
};

const playJoiningRoomSound = debounce(() => {
  const ringtone = new Audio(smileRingtoneUri);
  ringtone.volume = NOTIFICATION_VOLUME;
  gracefullyPlayAudio(ringtone);
}, DEBOUNCE_TIME_MS);

type HookOptions = {
  onDone?: () => void;
};

/**
 * Gracefully fetches the session appointment short ID and sets it in Jotai
 */
function useSetSessionAppointmentShortIdCallback() {
  const { providerShortId } = useParams<{ providerShortId: string }>();
  const { patientShortId } = useParams<{ patientShortId: string }>();
  const setSessionAppointmentShortId = useSetAtom(sessionAppointmentShortIdAtom);
  const [getTelehealthSessionInfo] = useGetTelehealthSessionInfoCallback();

  return useCallback(async () => {
    try {
      const { data } = await getTelehealthSessionInfo({
        variables: {
          patientShortId,
          providerShortId,
        },
      });
      setSessionAppointmentShortId(data?.telehealthSessionInfo?.appointment?.shortId);
    } catch (error) {
      logger.error(
        'Failed to fetch/set appointment short ID for session',
        {
          patientShortId,
          providerShortId,
        },
        error as Error,
      );
    }
  }, [getTelehealthSessionInfo, patientShortId, providerShortId, setSessionAppointmentShortId]);
}

const roomMutex = new DebugMutex('roomMutex');
export function useTransitionToMeetingRoom() {
  const setVisitorStateAtom = useSetAtom(visitorStateAtom);
  const connectRoom = useVisitorConnectRoomCallback();

  return useCallback(
    ({ onDone }: HookOptions = {}) => {
      // if multiple calls to transition to the meeting room happen simulatenously
      // the first one "wins" and the rest can just no-op.
      if (roomMutex.isLocked()) return;

      roomMutex.runExclusive(async () => {
        playJoiningRoomSound();
        setVisitorStateAtom(VisitorState.LOADING);

        // effectively sleep, need to await this way so that setTimeout runs async
        // otherwise, fire-and-forget will release the mutex before this timeout has elapsed!
        await new Promise((resolve) => setTimeout(resolve, LOAD_TIME_MS));

        const room = await connectRoom();

        if (!room) {
          setVisitorStateAtom(VisitorState.WAITING);
          return;
        }

        onDone?.();
      }, 'useTransitionToMeetingRoom');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [connectRoom],
  );
}

export function useWatchForRoomToken() {
  const syncClient = useAtomValue(twilioSyncClientAtom);
  const visitorAccessDocSid = useAtomValue(visitorAccessDocSidAtom);
  const transitionToMeetingRoom = useTransitionToMeetingRoom();
  const setRoomToken = useSetAtom(roomTokenAtom);
  const setZoomToken = useSetAtom(zoomTokenAtom);
  const setApiToken = useSetAtom(apiTokenAtom);
  const setSessionConversationSid = useSetAtom(sessionConversationSidAtom);
  const setSessionAppointmentShortId = useSetSessionAppointmentShortIdCallback();
  const setAdmissionAttempts = useSetAtom(admissionAttemptsAtom);
  const showAdmissionSystemNotification = useShowAdmissionSystemNotificationCallback();

  useEffect(
    function watchForRoomToken() {
      (async () => {
        if (!syncClient || !visitorAccessDocSid) return;
        logger.info('Initializing visitor-access document', {
          docSid: visitorAccessDocSid,
        });
        const visitorAccessDoc = await syncClient.document(visitorAccessDocSid);
        logger.info('Initialized visitor-access document', {
          docSid: visitorAccessDocSid,
        });
        const handleVisitorAccessDocUpdate = ({
          eventType,
          token,
          apiToken,
          conversationSid,
          zoomJwt,
          admissionTransactionId,
        }: VisitorAccessDocData) => {
          logger.info('Received visitor-access document update', {
            hasToken: !!token,
            eventType,
            conversationSid,
            admissionTransactionId,
          });
          if (admissionTransactionId) {
            admissionTrackingHistory.setTransactionId(admissionTransactionId);
          }
          setRoomToken(token);
          setZoomToken(zoomJwt);
          setApiToken(apiToken);
          setSessionConversationSid(conversationSid);

          switch (eventType) {
            case VisitorAccessDocEventType.GRANTED: {
              // This is not session blocking so fire and forget
              setSessionAppointmentShortId();
              setAdmissionAttempts((attempts) => attempts + 1);

              hideSystemNotificationsPermissionUserGesturePromptToast();
              showAdmissionSystemNotification();
              transitionToMeetingRoom();
              return;
            }
            case VisitorAccessDocEventType.DENIED:
              // TODO: Handle denied event type
              return;
            default:
              logger.error('Received an unknown visitor-access event type', {
                eventType,
                visitorAccessDocSid,
              });
              return;
          }
        };

        // Consuming initial data can lead to unexpected behavior - see TREE-844
        // const initialData = visitorAccessDoc.data as VisitorAccessDocData | undefined;
        // if (initialData?.eventType) {
        //   handleVisitorAccessDocUpdate(initialData);
        // }

        visitorAccessDoc.on('updated', ({ data }: { data: VisitorAccessDocData }) => {
          handleVisitorAccessDocUpdate(data);
        });
      })();
    },
    [
      syncClient,
      visitorAccessDocSid,
      transitionToMeetingRoom,
      setSessionConversationSid,
      setRoomToken,
      setApiToken,
      setSessionAppointmentShortId,
      setZoomToken,
      setAdmissionAttempts,
      showAdmissionSystemNotification,
    ],
  );
}
